import { createSelector } from '@reduxjs/toolkit';

import { RootState } from '@/app/model/store';

const selectTranscriptorState = (state: RootState) => state.transcriptor;

export const selectTranscriptorConsultationsByPatientId = createSelector(
  [selectTranscriptorState, (_: RootState, patientId: string) => patientId],
  (transcriptorState, patientId) => {
    const consultations = transcriptorState.consultations[patientId] || [];
    return consultations.filter((consultation) => !consultation.isArchived);
  },
);

export const selectHiddenTranscriptorConsultationsByPatientId = createSelector(
  [selectTranscriptorState, (_: RootState, patientId: string) => patientId],
  (transcriptorState, patientId) => {
    const consultations = transcriptorState.consultations[patientId] || [];
    return consultations.filter((consultation) => consultation.isArchived);
  },
);

export const selectTranscriptorConsultationsTotalByPatientId = createSelector(
  [selectTranscriptorState, (_: RootState, patientId: string) => patientId],
  (transcriptorState, patientId) =>
    transcriptorState.pagination[patientId]?.total ?? 0,
);

// The server total counts hidden recordings whenever the list was requested
// with them; the stored list is the single page the server returned, so
// subtracting its hidden entries gives the visible count without a refetch.
export const selectVisibleTranscriptorConsultationsTotalByPatientId =
  createSelector(
    [
      selectTranscriptorConsultationsTotalByPatientId,
      selectHiddenTranscriptorConsultationsByPatientId,
    ],
    (total, hiddenConsultations) =>
      Math.max(0, total - hiddenConsultations.length),
  );

// `loading` is global and flips on every poll, so a first-load spinner has to
// key on whether this patient's list has ever arrived.
export const selectTranscriptorConsultationsLoadedByPatientId = createSelector(
  [selectTranscriptorState, (_: RootState, patientId: string) => patientId],
  (transcriptorState, patientId) => patientId in transcriptorState.pagination,
);

export const selectTranscriptorLoading = createSelector(
  selectTranscriptorState,
  (transcriptorState) => transcriptorState.loading,
);

export const selectTranscriptorError = createSelector(
  selectTranscriptorState,
  (transcriptorState) => transcriptorState.error,
);

export const selectConsultationsLoading = createSelector(
  selectTranscriptorState,
  (transcriptorState) => transcriptorState.loading,
);

export const selectConsultationById = createSelector(
  [
    selectTranscriptorState,
    (_: RootState, consultationId: string) => consultationId,
  ],
  (transcriptorState, consultationId) => {
    // Search through all patient consultations
    const allConsultations = Object.values(
      transcriptorState.consultations,
    ).flat();
    const consultation = allConsultations.find(
      (consultation) => consultation.id === consultationId,
    );
    return consultation && !consultation.isArchived ? consultation : null;
  },
);

// Any of the patient's recordings, an archived one too.
export const selectPatientConsultationById = (
  state: RootState,
  patientId: string,
  consultationId: string,
) =>
  state.transcriptor.consultations[patientId]?.find(
    (consultation) => consultation.id === consultationId,
  );

// Recording selectors
export const selectIsRecording = createSelector(
  selectTranscriptorState,
  (transcriptorState) => transcriptorState.recording.isRecording,
);

// In the templates recording flow this field holds the encounter schema id
// (see useStartRecording); an empty string means the schema is unknown.
export const selectRecordingSchemaId = createSelector(
  selectTranscriptorState,
  (transcriptorState) => transcriptorState.recording.consultationType,
);

export const selectRecordingEncounterKind = createSelector(
  selectTranscriptorState,
  (transcriptorState) => transcriptorState.recording.encounterKind,
);

export const selectRecordingMode = createSelector(
  selectTranscriptorState,
  (transcriptorState) => transcriptorState.recording.recordingMode,
);

export const selectDictationDeadlineAt = createSelector(
  selectTranscriptorState,
  (transcriptorState) => transcriptorState.recording.dictationDeadlineAt,
);

export const selectIsPaused = createSelector(
  selectTranscriptorState,
  (transcriptorState) => transcriptorState.recording.isPaused,
);

export const selectRecordingTime = createSelector(
  selectTranscriptorState,
  (transcriptorState) => transcriptorState.recording.recordingTime,
);

export const selectMicrophoneName = createSelector(
  selectTranscriptorState,
  (transcriptorState) => transcriptorState.recording.microphoneName,
);

export const selectIsUploading = createSelector(
  selectTranscriptorState,
  (transcriptorState) => transcriptorState.recording.isUploading,
);

export const selectIsProcessing = createSelector(
  selectTranscriptorState,
  (transcriptorState) => transcriptorState.recording.isProcessing,
);

export const selectUploadProgress = createSelector(
  selectTranscriptorState,
  (transcriptorState) => transcriptorState.recording.uploadProgress,
);

export const selectConsultationDate = createSelector(
  selectTranscriptorState,
  (transcriptorState) => {
    const rawDate = transcriptorState.recording.consultationDate;
    if (!rawDate) {
      return new Date();
    }

    const parsedDate = new Date(rawDate);
    return Number.isNaN(parsedDate.getTime()) ? new Date() : parsedDate;
  },
);

export const selectTranscriptorRecording = createSelector(
  selectTranscriptorState,
  (transcriptorState) => transcriptorState.recording,
);

export const selectConsultationId = createSelector(
  selectTranscriptorState,
  (transcriptorState) => transcriptorState.recording.consultationId,
);

export const selectFragmentId = createSelector(
  selectTranscriptorState,
  (transcriptorState) => transcriptorState.recording.fragmentId,
);

export const selectPatientId = createSelector(
  selectTranscriptorState,
  (transcriptorState) => transcriptorState.recording.patientId,
);

export const selectIsOffline = createSelector(
  selectTranscriptorState,
  (transcriptorState) => transcriptorState.recording.isOffline,
);

export const selectLastSavedAt = createSelector(
  selectTranscriptorState,
  (transcriptorState) => transcriptorState.recording.lastSavedAt,
);

export const selectIsInterrupted = createSelector(
  selectTranscriptorState,
  (transcriptorState) => transcriptorState.recording.isInterrupted,
);

export const selectIsSignalLost = createSelector(
  selectTranscriptorState,
  (transcriptorState) => transcriptorState.recording.isSignalLost,
);

export const selectAudioIssue = createSelector(
  selectTranscriptorState,
  (transcriptorState) => transcriptorState.recording.audioIssue,
);

// Template selectors
export const selectTemplateDefinitions = createSelector(
  selectTranscriptorState,
  (transcriptorState) => transcriptorState.templates,
);

export const selectTemplateByType = (type: string) =>
  createSelector(
    selectTranscriptorState,
    (transcriptorState) => transcriptorState.templates[type] ?? null,
  );

export const selectConsultationByIdIncludeArchived = createSelector(
  [
    selectTranscriptorState,
    (_: RootState, consultationId: string) => consultationId,
  ],
  (transcriptorState, consultationId) => {
    const allConsultations = Object.values(
      transcriptorState.consultations,
    ).flat();
    return (
      allConsultations.find(
        (consultation) => consultation.id === consultationId,
      ) ?? null
    );
  },
);

export const selectRecordingTranscriptorV2 = (state: RootState) =>
  state.transcriptor.recording.transcriptorV2;

export const selectRecordingOrganizationId = (state: RootState) =>
  state.transcriptor.recording.organizationId;
